import { useState, useEffect, useCallback, useRef } from "react";
import {
  getConversations,
  getTeamMessages,
  getOrCreateDM,
  createGroup,
  getGeneral,
} from "../../../../api/teamChat";
import { getUserUuid } from "../utils/auth";

/**
 * useTeamChat — Manages team chat conversations and messages.
 *
 * Now listens for real-time events via custom events dispatched by
 * TeamPresenceContext instead of directly registering socket listeners.
 */
const useTeamChat = ({ teamSocket }) => {
  const [channels, setChannels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dms, setDms] = useState([]);
  const [currentTeamId, setCurrentTeamId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [lastReadTimestamp, setLastReadTimestamp] = useState(null);

  const userUuid = useRef(getUserUuid());
  const currentTeamIdRef = useRef(currentTeamId);

  useEffect(() => {
    currentTeamIdRef.current = currentTeamId;
  }, [currentTeamId]);

  // Tell the context which conversation is active (for unread tracking)
  useEffect(() => {
    teamSocket?.setActiveConversation?.(currentTeamId);
  }, [currentTeamId, teamSocket]);

  /* ── Load conversations ────────────────────────────────────────────── */
  const loadConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      await getGeneral();
      const res = await getConversations();
      const data = res?.data || {};
      setChannels(data.channels || []);
      setGroups(data.groups || []);
      setDms(data.dms || []);
    } catch (e) {
      console.error("Failed to load conversations:", e);
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  /* ── Load message history ──────────────────────────────────────────── */
  const loadMessages = useCallback(async (teamId, before = null) => {
    if (!teamId) return;
    setLoadingMessages(true);
    try {
      const res = await getTeamMessages(teamId, before, 50);
      const data = res?.data || {};
      const msgs = data.messages || [];
      if (before) {
        setMessages((prev) => [...msgs, ...prev]);
      } else {
        setMessages(msgs);
        if (!before) {
          // First load (not pagination) — find the last message already in read_by
          const lastRead = [...msgs]
            .reverse()
            .find((m) => m.read_by?.includes(userUuid.current));
          setLastReadTimestamp(lastRead?.timestamp || null);
        }
      }
      setHasMore(data.has_more || false);
    } catch (e) {
      console.error("Failed to load messages:", e);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (currentTeamId) {
      setMessages([]);
      loadMessages(currentTeamId);
    } else {
      setMessages([]);
      setHasMore(false);
    }
  }, [currentTeamId, loadMessages]);

  /* ── Load more (pagination) ────────────────────────────────────────── */
  const loadMore = useCallback(() => {
    if (!currentTeamId || !hasMore || loadingMessages) return;
    const oldest = messages[0];
    if (oldest) {
      loadMessages(currentTeamId, oldest.timestamp);
    }
  }, [currentTeamId, hasMore, loadingMessages, messages, loadMessages]);

  /* ── Real-time: new messages via custom event from context ─────────── */
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail;
      if (msg.team_id === currentTeamIdRef.current) {
        setMessages((prev) => {
          if (prev.some((m) => m.message_id === msg.message_id)) return prev;
          return [...prev, msg];
        });
      }
      _updateConversationPreview(msg.team_id, msg.content, msg.timestamp);
    };

    window.addEventListener("team:new-message", handler);
    return () => window.removeEventListener("team:new-message", handler);
  }, []);

  /* ── Real-time: read receipts via custom event ─────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      const data = e.detail;
      if (data.team_id !== currentTeamIdRef.current) return;

      setMessages((prev) =>
        prev.map((msg) => {
          if (data.message_ids.includes(msg.message_id)) {
            const readBy = msg.read_by || [];
            if (!readBy.includes(data.uuid)) {
              return { ...msg, read_by: [...readBy, data.uuid] };
            }
          }
          return msg;
        }),
      );
    };

    window.addEventListener("team:read-receipt", handler);
    return () => window.removeEventListener("team:read-receipt", handler);
  }, []);

  /* ── Real-time: message edited ─────────────────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      const data = e.detail;
      if (data.team_id !== currentTeamIdRef.current) return;

      setMessages((prev) =>
        prev.map((msg) =>
          msg.message_id === data.message_id
            ? {
                ...msg,
                content: data.content,
                edited: true,
                edited_at: data.edited_at,
              }
            : msg,
        ),
      );
    };

    window.addEventListener("team:message-edited", handler);
    return () => window.removeEventListener("team:message-edited", handler);
  }, []);

  /* ── Real-time: message deleted ────────────────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      const data = e.detail;
      if (data.team_id !== currentTeamIdRef.current) return;

      setMessages((prev) =>
        prev.map((msg) =>
          msg.message_id === data.message_id
            ? { ...msg, deleted: true, content: "" }
            : msg,
        ),
      );
    };

    window.addEventListener("team:message-deleted", handler);
    return () => window.removeEventListener("team:message-deleted", handler);
  }, []);

  /* ── Real-time: emoji reaction ── */
  useEffect(() => {
    const handler = (e) => {
      const data = e.detail;
      if (data.team_id !== currentTeamIdRef.current) return;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.message_id === data.message_id
            ? { ...msg, reactions: data.reactions }
            : msg,
        ),
      );
    };
    window.addEventListener("team:message-reacted", handler);
    return () => window.removeEventListener("team:message-reacted", handler);
  }, []);

  /* ── Real-time: message pinned/unpinned ── */
  useEffect(() => {
    const handler = (e) => {
      const data = e.detail;
      if (data.team_id !== currentTeamIdRef.current) return;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.message_id === data.message_id
            ? { ...msg, pinned: data.pinned }
            : msg,
        ),
      );
    };
    window.addEventListener("team:message-pinned", handler);
    return () => window.removeEventListener("team:message-pinned", handler);
  }, []);

  /* ── Real-time: AI stream start — insert placeholder ── */
  useEffect(() => {
    const handler = (e) => {
      const data = e.detail;
      if (data.team_id !== currentTeamIdRef.current) return;

      const placeholder = {
        message_id: data.message_id,
        team_id: data.team_id,
        sender_uuid: data.sender_uuid,
        sender_name: data.sender_name,
        type: "ai",
        content: "",
        mentions: [],
        attachments: [],
        read_by: [],
        edited: false,
        deleted: false,
        timestamp: data.timestamp,
        parent_message_id: data.parent_message_id,
        reactions: {},
        pinned: false,
        reply_to: null,
        citations: data.citations || [],
        _streaming: true,
      };

      setMessages((prev) => {
        if (prev.some((m) => m.message_id === data.message_id)) return prev;
        return [...prev, placeholder];
      });
    };

    window.addEventListener("team:ai-stream-start", handler);
    return () => window.removeEventListener("team:ai-stream-start", handler);
  }, []);

  /* ── Real-time: AI stream chunk — append token ── */
  useEffect(() => {
    const handler = (e) => {
      const data = e.detail;
      if (data.team_id !== currentTeamIdRef.current) return;

      setMessages((prev) =>
        prev.map((msg) =>
          msg.message_id === data.message_id
            ? { ...msg, content: msg.content + data.token }
            : msg,
        ),
      );
    };

    window.addEventListener("team:ai-stream-chunk", handler);
    return () => window.removeEventListener("team:ai-stream-chunk", handler);
  }, []);

  /* ── Real-time: AI stream end — replace with final message ── */
  useEffect(() => {
    const handler = (e) => {
      const data = e.detail;
      if (data.team_id !== currentTeamIdRef.current) return;

      setMessages((prev) =>
        prev.map((msg) =>
          msg.message_id === data.message_id
            ? { ...data.message, _streaming: false }
            : msg,
        ),
      );
    };

    window.addEventListener("team:ai-stream-end", handler);
    return () => window.removeEventListener("team:ai-stream-end", handler);
  }, []);

  /* ── Real-time: invited to new conversation ────────────────────────── */
  useEffect(() => {
    const handler = () => loadConversations();
    window.addEventListener("team:invited", handler);
    return () => window.removeEventListener("team:invited", handler);
  }, [loadConversations]);

  /* ── Helper: update conversation preview in sidebar data ───────────── */
  const _updateConversationPreview = useCallback(
    (teamId, content, timestamp) => {
      const updater = (prev) =>
        prev
          .map((c) =>
            c.team_id === teamId
              ? {
                  ...c,
                  last_message_ts: timestamp,
                  last_message_preview: (content || "").slice(0, 100),
                }
              : c,
          )
          .sort((a, b) => (b.last_message_ts || 0) - (a.last_message_ts || 0));

      setChannels(updater);
      setGroups(updater);
      setDms(updater);
    },
    [],
  );

  /* ── Create / find DM ──────────────────────────────────────────────── */
  const openDM = useCallback(
    async (targetUuid) => {
      try {
        const res = await getOrCreateDM(targetUuid);
        const data = res?.data || {};
        const conv = data.conversation;
        if (!conv) return null;

        if (data.created) {
          await loadConversations();
          teamSocket?.joinConversation(conv.team_id);
        }

        setCurrentTeamId(conv.team_id);
        return conv;
      } catch (e) {
        console.error("Failed to open DM:", e);
        return null;
      }
    },
    [loadConversations, teamSocket],
  );

  /* ── Create group ──────────────────────────────────────────────────── */
  const openGroup = useCallback(
    async (name, memberUuids) => {
      try {
        const res = await createGroup(name, memberUuids);
        const data = res?.data || {};
        const conv = data.conversation;
        if (!conv) return null;

        if (data.created) {
          await loadConversations();
          teamSocket?.joinConversation(conv.team_id);
          const others = memberUuids.filter((u) => u !== userUuid.current);
          if (others.length > 0) {
            teamSocket?.inviteToRoom(conv.team_id, others);
          }
        }

        setCurrentTeamId(conv.team_id);
        return conv;
      } catch (e) {
        console.error("Failed to create group:", e);
        return null;
      }
    },
    [loadConversations, teamSocket],
  );

  /* ── Select conversation ───────────────────────────────────────────── */
  const selectConversation = useCallback((teamId) => {
    setCurrentTeamId(teamId);
  }, []);

  const currentConversation =
    [...channels, ...groups, ...dms].find((c) => c.team_id === currentTeamId) ||
    null;

  return {
    channels,
    groups,
    dms,
    currentTeamId,
    currentConversation,
    messages,
    hasMore,
    loadingMessages,
    loadingConversations,
    loadConversations,
    loadMore,
    selectConversation,
    openDM,
    openGroup,
    userUuid: userUuid.current,
    lastReadTimestamp,
  };
};

export default useTeamChat;
